import { useState, useEffect, useMemo } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';
import { useSuddenLeaveReports, getBillingPeriodRange, type SuddenLeaveReport } from '@/hooks/useSuddenLeaveReports';
import { SuddenLeaveTable } from '@/components/admin/sudden-leave/SuddenLeaveTable';
import { SuddenLeaveConfirmDialog } from '@/components/admin/sudden-leave/SuddenLeaveConfirmDialog';
import { supabase } from '@/integrations/supabase/client';

// Generate period options (current month + 11 months back)
function generatePeriodOptions() {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    const { startDate, endDate } = getBillingPeriodRange(month, year);
    
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    
    options.push({
      value: `${month}-${year}`,
      label: `${thaiMonths[month - 1]} ${year + 543} (${startDate.day}/${startDate.month} - ${endDate.day}/${endDate.month})`,
    });
  }
  
  return options;
}

export default function AdminSuddenLeavePage() {
  const now = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState(`${now.getMonth() + 1}-${now.getFullYear()}`);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SuddenLeaveReport | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  
  const { reports, isLoading, fetchReports, reviewSuddenLeave } = useSuddenLeaveReports();
  const periodOptions = generatePeriodOptions();

  useEffect(() => {
    // Get current user email
    supabase.auth.getUser().then(({ data }) => {
      if (data.user?.email) {
        setUserEmail(data.user.email);
      }
    });
  }, []);

  useEffect(() => {
    const [month, year] = selectedPeriod.split('-').map(Number);
    fetchReports(month, year);
  }, [selectedPeriod, fetchReports]);

  const handleReview = (report: SuddenLeaveReport) => {
    setSelectedReport(report);
    setDialogOpen(true);
  };

  const handleConfirm = async (action: 'PAY' | 'NOT_PAY', note: string) => {
    if (!selectedReport) return;
    await reviewSuddenLeave(selectedReport.id, action, note, userEmail);
  };

  // Summary stats
  const stats = useMemo(() => {
    const pending = reports.filter(r => r.sudden_leave_reviewed_at === null).length;
    const paid = reports.filter(r => r.sudden_leave_reviewed_at !== null && !r.sudden_leave).length;
    const notPaid = reports.filter(r => r.sudden_leave_reviewed_at !== null && r.sudden_leave).length;
    return { pending, paid, notPaid, total: reports.length };
  }, [reports]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">ตรวจสอบการจ่ายเงินคลาสลากะทันหัน</h1>
                <p className="text-muted-foreground">
                  จัดการการจ่ายเงินครูสำหรับคลาสที่นักเรียนลากะทันหัน
                </p>
              </div>
            </div>

            {/* Period Selector */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">ช่วงเวลา:</span>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground">จำนวนทั้งหมด</div>
                <div className="text-2xl font-bold">{stats.total}</div>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground">รอการตรวจสอบ</div>
                <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground">จ่ายปกติ</div>
                <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground">ไม่จ่าย</div>
                <div className="text-2xl font-bold text-destructive">{stats.notPaid}</div>
              </div>
            </div>

            {/* Table */}
            <SuddenLeaveTable
              reports={reports}
              isLoading={isLoading}
              onReview={handleReview}
            />

            {/* Confirm Dialog */}
            <SuddenLeaveConfirmDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              report={selectedReport}
              onConfirm={handleConfirm}
            />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
